/*
 * Copyright (c) 2024 Cozy Intellect Inc.
 * File Path: packages/sharedlib/src/utils/store.util.ts
 */

import type { IStore } from '@repo/common';
import { debounceAsync } from '@repo/toolslib/src/utils/debounce.util';
import { isAxiosError } from 'axios';
import { get, type Writable, writable } from 'svelte/store';
import { getHandlerAsync } from '../sharedlib';
import constants from './constants';

/* Custom logger.*/
const _console = {
  trace: (filename: string, message: string, ...args: any[]) => {
    if (filename.includes('keychain')) {
      console.trace(`[${filename}]`, message, ...args);
    }
  },
  log: (filename: string, _message: string, ..._args: any[]) => {
    if (filename.includes('keychain') || filename.includes('user')) {
      // console.log(`[${filename}]`, _message, ..._args);
    }
  },
  error: (filename: string, message: string, ...args: any[]) => {
    console.error(`[${filename}]`, message, ...args);
  },
  warn: (filename: string, message: string, ...args: any[]) => {
    console.warn(`[${filename}]`, message, ...args);
  },
  info: (filename: string, message: string, ...args: any[]) => {
    if (filename.includes('keychain') || filename.includes('user')) {
      console.info(`[${filename}]`, message, ...args);
    }
  }
};

export type StatusEnum = 'init' | 'loading' | 'loaded' | 'error';

// Define the DataModel interface
export interface DataModel<T extends object> {
  item: T;
  lastUpdated: Date | number | null;
  nextUpdate: Date | number | null;
  status: StatusEnum;
  needsPatch: boolean;
}

const getDummyDataModel = <T extends object>(): DataModel<T> => {
  return {
    item: {} as T,
    lastUpdated: null,
    nextUpdate: null,
    status: 'init',
    needsPatch: false
  } as DataModel<T>;
};

export const loadDurableStore = async (
  filename: string,
  _forceGlobal: boolean = false
): Promise<IStore> => {
  const handler = await getHandlerAsync();

  try {
    return handler.storageController.loadDurableStore(filename);
  } catch (error) {
    if (error instanceof Error && error?.message?.includes('User ID not set')) {
      _console.log(filename, `Could not load durable store ${filename} - user ID not set`);
    } else {
      _console.error(filename, `Error loading durable store ${filename}:`, error);
    }
    throw error;
  }
};

export const loadStoredData = async <T extends object>(
  dummyItem: T,
  filename: string,
  writableItem: Writable<DataModel<T>>,
  reload: boolean = false
) => {
  const isKeychain = filename.endsWith('.keychain');
  const applyEntriesToStore = (
    entries: Array<[key: string, value: DataModel<T>[keyof DataModel<T>]]>
  ) => {
    const dummyDataModel: DataModel<T> = getDummyDataModel<T>();

    writableItem.update((dataModel) => {
      dataModel = { ...dummyDataModel, ...dataModel };
      for (const [key, value] of entries) {
        if (key == 'item') {
          dataModel.item = { ...dummyItem, ...(value as T) };
          // Rehydrate optional tokens if they were omitted during persistence.
          if (filename.includes('keychain')) {
            const itemWithTokens: any = dataModel.item;
            itemWithTokens.apiToken = itemWithTokens.apiToken ?? itemWithTokens.idToken ?? null;
            itemWithTokens.apiTokenM = itemWithTokens.apiTokenM ?? itemWithTokens.idToken ?? null;
            dataModel.item = itemWithTokens;
          }
        } else {
          (dataModel as any)[key] = value;
        }
      }
      return {
        ...dataModel,
        status: 'loaded'
      };
    });
  };

  // Loads the data into the ephemeral store from the durable store.
  // To avoid an empty store, it uses the dummy object as default values.
  _console.log(filename, `Loading stored data with params:`, {
    dummyItem,
    filename,
    writableItem,
    reload
  });

  const store: IStore = await loadDurableStore(filename);
  _console.log(filename, `Getting entries for store ${filename}...`);
  const entries = await store.entries<DataModel<T>[keyof DataModel<T>]>();

  let metaEntries: Array<[key: string, value: DataModel<T>[keyof DataModel<T>]]> = [];
  if (isKeychain) {
    const metaStoreName = `${filename}.meta`;
    const metaStore = await loadDurableStore(metaStoreName);
    metaEntries = await metaStore.entries<DataModel<T>[keyof DataModel<T>]>();
    _console.log(filename, `Entries for store ${filename}:`, entries);
    _console.log(metaStoreName, `Entries for meta store ${metaStoreName}:`, metaEntries);
  } else {
    _console.log(filename, `Entries for store ${filename}:`, entries);
  }

  // Apply initial entries immediately.
  applyEntriesToStore([...entries, ...metaEntries]);

  // Background reload to pick up fresh data without blocking initial paint.
  const shouldReload =
    reload && constants.PLATFORM !== 'windows' && constants.PLATFORM !== 'desktop';
  if (shouldReload) {
    const reloadFn = async () => {
      _console.log(filename, `Background reloading store ${filename}...`);
      const timeoutMs = 1000;
      const timeoutPromise = new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error(`store.reload timeout after ${timeoutMs}ms`)), timeoutMs)
      );
      try {
        await Promise.race([store.reload(), timeoutPromise]);
        const freshEntries = await store.entries<DataModel<T>[keyof DataModel<T>]>();

        let freshMetaEntries: Array<[key: string, value: DataModel<T>[keyof DataModel<T>]]> = [];
        if (isKeychain) {
          const metaStoreName = `${filename}.meta`;
          const metaStore = await loadDurableStore(metaStoreName);
          await metaStore.reload();
          freshMetaEntries = await metaStore.entries<DataModel<T>[keyof DataModel<T>]>();
          _console.log(
            metaStoreName,
            `Background reload complete for ${metaStoreName}`,
            freshMetaEntries
          );
        }

        _console.log(filename, `Background reload complete for ${filename}`, freshEntries);
        applyEntriesToStore([...freshEntries, ...freshMetaEntries]);
      } catch (error) {
        _console.warn(filename, `Background reload skipped/failed:`, error);
      }
    };

    if (isKeychain) {
      _console.log(filename, 'Reloading keychain store (blocking)');
      await reloadFn();
    } else {
      _console.log(filename, 'Reloading normal store');
      const scheduleReload =
        typeof requestIdleCallback !== 'undefined'
          ? (fn: () => void) => requestIdleCallback(fn)
          : (fn: () => void) => setTimeout(fn, 0);
      scheduleReload(reloadFn);
    }
  }
  return true;
};

type Handler<G extends object> = (data: Writable<DataModel<G>>) => Promise<void>;

export const getPersist = <G extends object>(dummyItem: G, filename: string): Handler<G> => {
  return async (data: Writable<DataModel<G>>, force: boolean = false) => {
    return await persistStore(dummyItem, data, filename, force);
  };
};

const filterItem = <T extends object>(item: Partial<T>, dummyItem: T): Partial<T> => {
  const newItem: Partial<T> = {};
  Object.keys(dummyItem || {}).forEach((key) => {
    newItem[key as keyof T] = item[key as keyof T];
  });
  return newItem;
};

const KEYCHAIN_UTF16_BYTE_LIMIT = 2560;
const KEYCHAIN_OPTIONAL_AUTH_KEYS = ['apiToken', 'apiTokenM'];

const sanitizeKeychainItem = <T extends object>(
  filename: string,
  dummyItem: T,
  filteredItem: Partial<T>
): Partial<T> => {
  if (!filename.includes('keychain')) {
    return filteredItem;
  }

  // Drop optional auth fields up front (idToken is equivalent to apiToken/apiTokenM).
  const trimmedItem = { ...filteredItem } as Record<string, unknown>;
  KEYCHAIN_OPTIONAL_AUTH_KEYS.forEach((key) => delete trimmedItem[key]);

  // Also drop optional auth fields from the dummy item for this storage target.
  const trimmedDummy = { ...dummyItem } as Record<string, unknown>;
  KEYCHAIN_OPTIONAL_AUTH_KEYS.forEach((key) => delete trimmedDummy[key]);

  const payload = { ...trimmedDummy, ...trimmedItem };
  const payloadStr = JSON.stringify(payload);
  const utf16Bytes = payloadStr.length * 2; // UTF-16 uses 2 bytes per code unit
  const isWindowsPlatform = constants.PLATFORM === 'windows' || constants.PLATFORM === 'desktop';

  if (isWindowsPlatform) {
    // On Windows, payloads are split into separate entries: idToken, refreshToken, and meta.
    // Validate individual entry sizes instead of merged payload.
    const auth = trimmedItem as Record<string, unknown>;
    const idTokenStr = auth.idToken ? JSON.stringify(auth.idToken) : '';
    const refreshTokenStr = auth.refreshToken ? JSON.stringify(auth.refreshToken) : '';

    // Meta contains remaining fields
    const metaRest = { ...auth };
    delete metaRest.idToken;
    delete metaRest.refreshToken;
    const metaStr = JSON.stringify(metaRest);

    const idTokenBytes = idTokenStr.length * 2;
    const refreshTokenBytes = refreshTokenStr.length * 2;
    const metaBytes = metaStr.length * 2;

    _console.warn(
      filename,
      `Keychain payload split entries - idToken=${idTokenBytes}B refreshToken=${refreshTokenBytes}B meta=${metaBytes}B (total would be ${utf16Bytes}B)`,
      { idToken: `${idTokenBytes}B`, refreshToken: `${refreshTokenBytes}B`, meta: `${metaBytes}B` }
    );

    // Check if individual entries exceed limit
    if (idTokenBytes > KEYCHAIN_UTF16_BYTE_LIMIT) {
      _console.error(
        filename,
        `Keychain idToken exceeds UTF-16 byte limit (${idTokenBytes} > ${KEYCHAIN_UTF16_BYTE_LIMIT}).`
      );
    }
    if (refreshTokenBytes > KEYCHAIN_UTF16_BYTE_LIMIT) {
      _console.error(
        filename,
        `Keychain refreshToken exceeds UTF-16 byte limit (${refreshTokenBytes} > ${KEYCHAIN_UTF16_BYTE_LIMIT}).`
      );
    }
    if (metaBytes > KEYCHAIN_UTF16_BYTE_LIMIT) {
      _console.error(
        filename,
        `Keychain meta exceeds UTF-16 byte limit (${metaBytes} > ${KEYCHAIN_UTF16_BYTE_LIMIT}).`
      );
    }
  }

  return trimmedItem as Partial<T>;
};

const persistStore = async <T extends object>(
  dummyItem: T,
  dataWritable: Writable<DataModel<T>>,
  filename: string,
  force: boolean = false
) => {
  if (!dataWritable) {
    throw Error(`Could not find writable store for persistStore: ${filename}`);
  }
  const dummyDataModel = getDummyDataModel<T>();
  const store: IStore = await loadDurableStore(filename);
  const isKeychain = filename.endsWith('.keychain');
  const metaStoreName = `${filename}.meta`;
  const metaStore: IStore | null = isKeychain ? await loadDurableStore(metaStoreName) : null;

  // OPTIMIZATION: Read current state once
  const entries: Array<[key: string, value: DataModel<T>]> = await store.entries<DataModel<T>>();
  const metaEntries: Array<[key: string, value: DataModel<T>]> = metaStore
    ? await metaStore.entries<DataModel<T>>()
    : [];

  // Prepare new data to persist
  const dataModel = filterItem<DataModel<T>>(get(dataWritable), dummyDataModel);
  const newMainStoreData: Record<string, any> = {};
  const newMetaStoreData: Record<string, any> = {};

  for (const key of Object.keys(dataModel)) {
    if (key == 'item') {
      const { item } = dataModel;
      if (filename.includes('keychain')) {
        // For keychain, we want to persist the exact item we have in memory,
        // skipping the filterItem logic which relies on dummyItem keys.
        // This ensures we don't accidentally drop fields (like new metadata)
        // just because dummyItem is out of sync.
        let itemToPersist = { ...item };
        itemToPersist = sanitizeKeychainItem(filename, dummyItem, itemToPersist);
        
        const trimmedDummy = { ...dummyItem } as Record<string, unknown>;
        KEYCHAIN_OPTIONAL_AUTH_KEYS.forEach((key) => delete trimmedDummy[key]);
        
        newMainStoreData['item'] = { ...trimmedDummy, ...itemToPersist };
      } else {
        // Ensure that only keys present in dummyItem are allowed in item
        let filteredItem = filterItem<T>(item || ({} as T), dummyItem);
        newMainStoreData['item'] = { ...dummyItem, ...filteredItem };
      }
    } else {
      const value = (dataModel as any)[key];
      if (metaStore) {
        newMetaStoreData[key] = value;
      } else {
        newMainStoreData[key] = value;
      }
    }
  }

  // OPTIMIZATION: Check if data actually changed before persisting
  const currentMainData = Object.fromEntries(entries);
  const currentMetaData = metaStore ? Object.fromEntries(metaEntries) : {};

  // Use stable stringify to ensure consistent property order
  const stableStringify = (obj: any) => {
    const sortedKeys = Object.keys(obj).sort();
    return JSON.stringify(obj, sortedKeys);
  };

  const mainDataChanged = stableStringify(newMainStoreData) !== stableStringify(currentMainData);
  const metaDataChanged = metaStore && stableStringify(newMetaStoreData) !== stableStringify(currentMetaData);

  // DEBUG: Log when keychain is being saved
  if (filename.includes('keychain')) {
    if (mainDataChanged || force) {
      _console.log(filename, `Keychain data changed, persisting:`, {
        current: currentMainData,
        new: newMainStoreData
      });
    } else {
      _console.log(filename, `Keychain data unchanged, skipping persist`);
    }
  }

  // OPTIMIZATION: Use clear() + bulk set pattern instead of individual delete/set calls
  // This reduces API calls from 8+ to 4 (entries, clear, set, save)
  if (mainDataChanged || force) {
    await store.clear();
    for (const [key, value] of Object.entries(newMainStoreData)) {
      await store.set(key, value);
      if (filename.includes('keychain') && key === 'item') {
        _console.log(filename, 'Persisting item:', value);
      } else if (!filename.includes('keychain') && key === 'item') {
        _console.log(filename, 'Persisting item:', value);
      }
    }
    await store.save();
  }

  if (metaStore && (metaDataChanged || force)) {
    await metaStore.clear();
    for (const [key, value] of Object.entries(newMetaStoreData)) {
      await metaStore.set(key, value);
    }
    await metaStore.save();
  }

  // OPTIMIZATION: Removed redundant entries() call that was only used for logging
  // (Previously at line 309-312, called entries() again after save())
};

const SECONDS = 1000;
const MINUTES = 60 * SECONDS;
const RETRY_TIMEOUT = 30 * SECONDS;
const UPDATE_TIMEOUT = 5 * MINUTES;

export function createStoreHandle<T extends object>(
  filename: string,
  dummyItem: T,
  options?: { updateTimeout: number; retryTimeout: number },
  fetchItem?: () => Promise<Omit<DataModel<T>, 'status' | 'lastUpdated'> | undefined>,
  onFetchError?: (message: string) => void
) {
  const defaultDataModel: DataModel<T> = {
    item: { ...dummyItem },
    lastUpdated: null,
    nextUpdate: null,
    status: 'init',
    needsPatch: false
  };

  const store = writable<DataModel<T>>({ ...defaultDataModel });

  const persistItem = debounceAsync(
    async (force: boolean = false) => {
      await persistStore(dummyItem, store, filename, force);
    },
    'persistItem',
    100
  );

  /*
  Sets the item (partial update) and optionally sets the status and needsPatch value.
  Eventually persists (based on debounce logic) to local.
   */
  const setItem = async (
    data: Partial<T>,
    immediatePersist = false,
    status?: StatusEnum,
    needsPatch?: boolean
  ) => {
    if (Object.keys(data).length === 0) {
      return;
    }

    // OPTIMIZATION: Check if data actually changed before updating store
    // This prevents unnecessary store updates and persist calls
    const currentData = get(store);
    const newItemData = { ...currentData.item, ...data };
    const itemChanged = JSON.stringify(newItemData) !== JSON.stringify(currentData.item);
    const statusChanged = status !== undefined && status !== currentData.status;
    const patchChanged = needsPatch !== undefined && needsPatch !== currentData.needsPatch;

    if (!itemChanged && !statusChanged && !patchChanged) {
      _console.log(filename, `setItem: No changes detected, skipping update`);
      return;
    }

    store.update(($dataModel) => {
      $dataModel.item = { ...$dataModel.item, ...data };
      if (status !== undefined) {
        $dataModel.status = status;
      }
      if (needsPatch !== undefined) {
        $dataModel.needsPatch = needsPatch;
      }
      return $dataModel;
    });
    if (immediatePersist) {
      _console.log(filename, `Persisting item immediately, data:`, get(store));
      try {
        await persistItem(immediatePersist);
      } catch (error) {
        if ((error as any)?.message?.includes('User ID not set')) {
          _console.log(filename, `Could not persist ${filename} - user ID not set`);
        } else {
          _console.error(filename, `Error persisting item:`, error);
        }
      }
    } else {
      _console.log(filename, `Debouncing persistItem`);
      try {
        await debounceAsync(persistItem, 'persistItem', 1500)();
      } catch (error) {
        if ((error as any)?.message?.includes('User ID not set')) {
          _console.log(filename, `Could not persist ${filename} - user ID not set`);
        } else {
          _console.error(filename, `Error persisting item:`, error);
        }
      }
    }
  };

  const scheduleUpdate = async (time_ms: number) => {
    store.update((dataModel) => ({
      ...dataModel,
      nextUpdate: new Date(Date.now() + time_ms).getTime(),
      status: 'loaded'
    }));
    // Ensure we persist right away
    try {
      await persistItem();
    } catch (error) {
      if ((error as any)?.message?.includes('User ID not set')) {
        _console.log(filename, `Could not persist ${filename} - user ID not set`);
      } else {
        _console.error(filename, `Error persisting item:`, error);
      }
    }
  };

  const hardResetItem = async () => {
    // Make sure the item gets persisted to storage by directly clearing stores
    // We need to force clear rather than using persistItem() because the optimization
    // in persistStore() might skip clearing if data appears unchanged
    try {
      const durableStore: IStore = await loadDurableStore(filename);
      await durableStore.clear();
      await durableStore.set('item', { ...dummyItem });
      await durableStore.save();

      // Also clear meta store for keychain
      const isKeychain = filename.endsWith('.keychain');
      if (isKeychain) {
        const metaStoreName = `${filename}.meta`;
        const metaStore: IStore = await loadDurableStore(metaStoreName);
        await metaStore.clear();
        await metaStore.save();
        _console.log(metaStoreName, `Cleared meta store ${metaStoreName}`);
      }

      _console.log(filename, `Reset and cleared store ${filename}`);
    } catch (error) {
      // If user ID is not set (e.g., during logout), we can't access the durable store
      // This is fine - it means there's nothing to clear anyway
      if ((error as any)?.message?.includes('User ID not set')) {
        _console.log(filename, `Skipping durable store clear - user ID not set`);
      } else {
        _console.error(filename, `Error resetting item:`, error);
        throw error;
      }
    }
  };

  const normalResetItem = async () => {
    _console.log(filename, `Resetting item to`, dummyItem);
    store.set({
      item: { ...dummyItem },
      lastUpdated: null,
      nextUpdate: null,
      status: 'init',
      needsPatch: false
    });
  }

  const resetItem = async (hardReset: boolean = false) => {
    _console.log(filename, `Resetting item to`, dummyItem);
    store.set({
      item: { ...dummyItem },
      lastUpdated: null,
      nextUpdate: null,
      status: 'init',
      needsPatch: false
    });
    if (hardReset) {
      await hardResetItem();
    } else {
      try {
        await normalResetItem();
      } catch (error) {
        _console.error(filename, `Error resetting item:`, error);
        throw error;
      }
    }
  };

  const loadStoredItem = async (
    force: boolean = false,
    reload: boolean = false
  ): Promise<T | null> => {
    const $store = get(store);
    if (filename.includes('keychain')) {
      _console.info(filename, `Loading stored item ${filename} with params:`, {
        force,
        reload
      });
    }
    if ($store.status !== 'init' && !force) {
      return null;
    }
    let success: boolean = false;
    try {
      success = await loadStoredData(dummyItem, filename, store, reload);
    } catch (error) {
      if ((error as any)?.message?.includes('User ID not set')) {
        _console.log(filename, `Could not load stored item ${filename} - user ID not set`);
        return null;
      } else {
        _console.error(filename, `Error loading stored item ${filename}:`, error);
      }
      throw error;
    }
    if (success) {
      const dataModel = get(store);
      return dataModel.item;
    }

    if (fetchItem) {
      // If fetchItem is defined, we want to fetch the outdated items now.
      await updateOutdatedItem();
      const dataModel = get(store);
      return dataModel.item;
    }
    return null;
  };

  const updateItem = async (): Promise<boolean> => {
    if (!fetchItem) {
      throw Error('Update Item called when fetchItem is not defined');
    }
    const prevStatus = get(store).status;
    // TODO: Replace this with a PUT to the server
    store.update((dataModel) => ({
      ...dataModel,
      status: 'loading'
    }));

    try {
      const fetchedData = await fetchItem();
      if (fetchedData !== undefined) {
        store.update((dataModel) => ({
          ...dataModel,
          ...fetchedData,
          item: {
            ...dataModel.item,
            ...fetchedData.item
          },
          lastUpdated: new Date().getTime(),
          nextUpdate: new Date(Date.now() + (options?.updateTimeout ?? UPDATE_TIMEOUT)).getTime(),
          status: 'loaded'
        }));
        // Save the newly fetched data.
        try {
          await persistItem();
          _console.log(filename, `Item successfully fetched, updated, and persisted.`);
        } catch (error) {
          if ((error as any)?.message?.includes('User ID not set')) {
            _console.log(filename, `Could not persist ${filename} - user ID not set`);
          } else {
            _console.error(filename, `Error persisting item:`, error);
          }
        }
        return true;
      } else {
        _console.log(filename, `Got undefined for fetchItem`);
        store.update((dataModel) => ({
          ...dataModel,
          status: prevStatus
        }));
      }
    } catch (error) {
      if (isAxiosError(error)) {
        if (onFetchError) {
          onFetchError(error.message);
        } else {
          _console.error(
            filename,
            `Axios error fetching item [${error?.config?.url}]: ${error.message}}`
          );
        }
      } else if ((error as any)?.message?.includes('User ID not set')) {
        _console.log(filename, `Could not fetch item ${filename} - user ID not set`);
      } else {
        _console.error(filename, `Unknown error fetching item:`, error);
      }

      store.update((dataModel) => ({
        ...dataModel,
        nextUpdate: new Date(Date.now() + (options?.retryTimeout ?? RETRY_TIMEOUT)).getTime(),
        status: 'error'
      }));
    }
    return false;
  };

  const updateOutdatedItem = async () => {
    const $item = get(store);
    const { lastUpdated, nextUpdate, status } = $item;
    // @ts-expect-error Date instantiation
    const nextUpdateTimestamp = new Date(nextUpdate).getTime();
    if (
      status === 'loaded' &&
      (!lastUpdated || !nextUpdate || new Date().getTime() >= nextUpdateTimestamp)
    ) {
      _console.log(filename, `Updating outdated item`, $item);
      await updateItem();
    }
  };

  const itemStore = writable<T>(get(store).item, (set) => {
    const unsubscribe = store.subscribe(($itemDataModel) => {
      set($itemDataModel.item);
    });
    return unsubscribe;
  });

  // Override the set method to fully replace the item (not merge)
  itemStore.set = (value: T) => {
    store.update(($dataModel) => ({
      ...$dataModel,
      item: value
    }));
  };

  const waitForCondition = async (
    predicate: (dataModel: DataModel<T>) => boolean
  ): Promise<DataModel<T>> => {
    return new Promise((resolve) => {
      let unsubscribe: () => void = () => { };
      const checkCondition = (dataModel: DataModel<T>) => {
        if (predicate(dataModel)) {
          unsubscribe();
          resolve(dataModel);
        }
      };
      unsubscribe = store.subscribe(checkCondition);
    });
  };

  return {
    store, // The writable store itself
    itemStore, // Write access to the user
    setItem, // Method to set the store's value
    resetItem, // Method to reset the store to its default value
    loadStoredItem, // Method to load persisted data into the store
    scheduleUpdate, // Method to schedule updating next page load
    updateItem, // Method to fetch and update the store's value from the remote source
    updateOutdatedItem, // Method to conditionally update outdated items
    waitForCondition // Method to wait for any condition and return the DataModel
  };
}
